// File: /src/components/InvoicesTable.js

import React, { useState } from 'react';
import TableSortHeader from './TableSortHeader';
import { useSortableData } from '../hooks/useSortableData';
import '../styles/GridClients.css';

const InvoicesTable = ({
	invoices,
	services,
	clients,
	openClientModal,
	selectedInvoices,
	toggleSelectInvoice,
	isShowingArchived,
}) => {
	// Configuração inicial para ordenação das faturas
	const initialConfig = { key: 'id', direction: 'asc' };

	// Mapeamento para incluir informações relacionadas (projeto e cliente)
	const invoicesWithInfo = React.useMemo(() => {
		return invoices.map((invoice) => {
			const service = services.find((s) => s.id === invoice.projectId);
			const client = service
				? clients.find((c) => c.id === service.clientId)
				: null;
			return {
				...invoice,
				serviceName: service?.name || '-',
				clientName: client?.name || '-',
			};
		});
	}, [invoices, services, clients]);

	// Hook para ordenação usando o array mapeado
	const { sortedItems, sortConfig, handleSort } = useSortableData(
		invoicesWithInfo,
		initialConfig
	);

	// Manipulador para ordenação especial dos campos "client" e "service"
	const handleCustomSort = (key) => {
		if (key === 'client') {
			handleSort('clientName');
		} else if (key === 'service') {
			handleSort('serviceName');
		} else {
			handleSort(key);
		}
	};

	const tableRowStyle = { cursor: 'pointer' };

	// Estado para controle de visibilidade da tabela (salvo no localStorage)
	const [showTable, setShowTable] = useState(() => {
		const savedState = localStorage.getItem('invoiceTableVisible');
		return savedState !== null ? JSON.parse(savedState) : true;
	});

	const toggleTableVisibility = () =>
		setShowTable((prev) => {
			const newState = !prev;
			localStorage.setItem('invoiceTableVisible', JSON.stringify(newState));
			return newState;
		});

	// Função para selecionar/desselecionar todos os itens
	const handleSelectAll = (event) => {
		const allIds = sortedItems.map((invoice) => invoice.id);
		if (event.target.checked) {
			allIds.forEach((id) => {
				if (!selectedInvoices.includes(id)) {
					toggleSelectInvoice(id);
				}
			});
		} else {
			allIds.forEach((id) => {
				if (selectedInvoices.includes(id)) {
					toggleSelectInvoice(id);
				}
			});
		}
	};

	return (
		<div className="table-section">
			<h3 onClick={toggleTableVisibility} style={{ cursor: 'pointer' }}>
				Faturas {showTable ? '-' : '+'}
			</h3>
			{showTable && (
				<div className="table-container">
					<table>
						<thead>
							<tr>
								{/* Coluna para o checkbox geral */}
								<th>
									<input
										type="checkbox"
										className="grid-client-checkbox"
										checked={
											sortedItems.length > 0 &&
											sortedItems.every((invoice) =>
												selectedInvoices.includes(
													invoice.id
												)
											)
										}
										onChange={handleSelectAll}
									/>
								</th>
								<TableSortHeader
									label="ID"
									sortKey="id"
									currentSortConfig={sortConfig}
									onSort={handleSort}
								/>
								<TableSortHeader
									label="Descrição"
									sortKey="description"
									currentSortConfig={sortConfig}
									onSort={handleSort}
								/>
								<TableSortHeader
									label="Projeto"
									sortKey="service"
									currentSortConfig={{
										key:
											sortConfig.key === 'serviceName'
												? 'service'
												: sortConfig.key,
										direction: sortConfig.direction,
									}}
									onSort={handleCustomSort}
								/>
								<TableSortHeader
									label="Cliente"
									sortKey="client"
									currentSortConfig={{
										key:
											sortConfig.key === 'clientName'
												? 'client'
												: sortConfig.key,
										direction: sortConfig.direction,
									}}
									onSort={handleCustomSort}
								/>
								<TableSortHeader
									label="Valor"
									sortKey="value"
									currentSortConfig={sortConfig}
									onSort={handleSort}
								/>
								<TableSortHeader
									label="Data de Vencimento"
									sortKey="due_date"
									currentSortConfig={sortConfig}
									onSort={handleSort}
								/>
								<TableSortHeader
									label="Status"
									sortKey="status"
									currentSortConfig={sortConfig}
									onSort={handleSort}
								/>
							</tr>
						</thead>
						<tbody>
							{sortedItems.length === 0 ? (
								<tr>
									<td colSpan="8">
										{isShowingArchived 
											? "Nenhuma fatura arquivada."
											: "Nenhuma fatura registrada."}
									</td>
								</tr>
							) : (
								sortedItems.map((invoice) => {
									const service = services.find(
										(s) => s.id === invoice.projectId
									);
									const client = service
										? clients.find(
												(c) => c.id === service.clientId
										  )
										: null;
									return (
										<tr
											key={invoice.id}
											style={tableRowStyle}
											onClick={() =>
												client &&
												openClientModal(client)
											}
										>
											{/* Checkbox para seleção individual */}
											<td>
												<input
													type="checkbox"
													className="grid-client-checkbox"
													checked={selectedInvoices.includes(
														invoice.id
													)}
													onClick={(e) =>
														e.stopPropagation()
													}
													onChange={() =>
														toggleSelectInvoice(
															invoice.id
														)
													}
												/>
											</td>
											<td>{invoice.id}</td>
											<td>
												{invoice.description || '-'}
											</td>
											<td>{invoice.serviceName}</td>
											<td>{invoice.clientName}</td>
											<td>
												{Number(
													invoice.value
												).toLocaleString('pt-BR', {
													style: 'currency',
													currency: 'BRL',
												})}
											</td>
											<td>
												{new Date(
													invoice.due_date
												).toLocaleDateString('pt-BR')}
											</td>
											<td>
												<span
													className={`status-badge ${
														invoice.status ===
														'paid'
															? 'status-paid'
															: invoice.status ===
															  'pending'
															? 'status-pending'
															: invoice.status ===
															  'overdue'
															? 'status-overdue'
															: ''
													}`}
												>
													{invoice.status === 'paid'
														? 'PAGA'
														: invoice.status ===
														  'pending'
														? 'PENDENTE'
														: invoice.status ===
														  'overdue'
														? 'ATRASADA'
														: invoice.status.toUpperCase()}
												</span>
											</td>
										</tr>
									);
								})
							)}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
};

export default InvoicesTable;
